import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ClipboardCheck, FileText, Receipt, FileSignature, CheckCircle2, Send, Eye, DollarSign,
  Truck, MapPin, AlertCircle, ShieldCheck, ShieldOff, Upload, MailOpen, Clock, Activity,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Render style for each event kind/status. */
const STYLE = {
  // Workflow status
  'Submitted':            { icon: ClipboardCheck, color: 'text-[#D4AF37]', label: 'Service Request Submitted' },
  'Scheduled':            { icon: Clock,          color: 'text-cyan-300',  label: 'Job Scheduled' },
  'Technician Assigned':  { icon: ShieldCheck,    color: 'text-cyan-300',  label: 'Technician Assigned' },
  'Dispatched':           { icon: Truck,          color: 'text-emerald-300', label: 'Dispatched' },
  'En Route':             { icon: Truck,          color: 'text-emerald-300', label: 'Technician En Route' },
  'On Site':              { icon: MapPin,         color: 'text-emerald-300', label: 'Technician Arrived' },
  'Waiting Approval':     { icon: AlertCircle,    color: 'text-[#D4AF37]', label: 'Waiting Approval' },
  'Completed':            { icon: CheckCircle2,   color: 'text-emerald-400', label: 'Work Completed' },
  'Invoiced':             { icon: Send,           color: 'text-emerald-400', label: 'Invoice Sent' },
  'Paid':                 { icon: DollarSign,     color: 'text-emerald-400', label: 'Invoice Paid' },
  // PO events (kind=...)
  'po.added':             { icon: FileText,       color: 'text-[#D4AF37]', label: 'PO Added' },
  'po.edited':            { icon: FileText,       color: 'text-[#D4AF37]', label: 'PO Updated' },
  'po.received':          { icon: ShieldCheck,    color: 'text-emerald-300', label: 'PO Received' },
  'po.rejected':          { icon: AlertCircle,    color: 'text-red-300',     label: 'PO Rejected' },
  'po.requested':         { icon: MailOpen,       color: 'text-cyan-300',    label: 'PO Requested from Billing' },
  'po.needed':            { icon: AlertCircle,    color: 'text-[#D4AF37]', label: 'PO Needed' },
  'po.attached_invoice':  { icon: FileSignature,  color: 'text-emerald-300', label: 'PO Attached to Invoice' },
  'po.uploaded':          { icon: Upload,         color: 'text-[#D4AF37]', label: 'PO Document Uploaded' },
  'po.not_required_override': { icon: ShieldOff,  color: 'text-[#A3A3A3]', label: 'PO Marked Not Required (admin override)' },
  // Estimate / invoice (hook from server.py — when wired)
  'estimate.sent':        { icon: Send,           color: 'text-cyan-300',    label: 'Estimate Sent' },
  'estimate.approved':    { icon: CheckCircle2,   color: 'text-emerald-300', label: 'Estimate Approved' },
  'invoice.sent':         { icon: Send,           color: 'text-cyan-300',    label: 'Invoice Sent' },
  'invoice.viewed':       { icon: Eye,            color: 'text-cyan-200',    label: 'Invoice Viewed' },
  'invoice.paid':         { icon: DollarSign,     color: 'text-emerald-400', label: 'Invoice Paid' },
  // Tech acceptance
  'tech.accepted':        { icon: ShieldCheck,    color: 'text-emerald-300', label: 'Technician Accepted Job' },
};

const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

const JobTimeline = ({ workOrderId, mode = 'pm' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workOrderId) return;
    const url = mode === 'admin'
      ? `${API}/admin/pm/work-orders/${workOrderId}/timeline`
      : `${API}/pm/work-orders/${workOrderId}/timeline`;
    axios.get(url, { withCredentials: true })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [workOrderId, mode]);

  if (loading) return <div className="text-[#525252] text-sm">Loading timeline…</div>;

  // Sort newest first
  const sorted = items.slice().sort((a, b) => new Date(b.at || b.created_at || 0) - new Date(a.at || a.created_at || 0));

  return (
    <div data-testid="job-timeline" className="bg-[#141414] border border-[#262626] rounded-md p-5">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-4 h-4 text-[#D4AF37]" />
        <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.22em] uppercase">Activity Timeline</p>
        <span className="text-[#525252] text-xs ml-auto">{sorted.length} event{sorted.length === 1 ? '' : 's'}</span>
      </div>
      {sorted.length === 0 ? (
        <p data-testid="job-timeline-empty" className="text-[#525252] text-sm py-2">No activity yet.</p>
      ) : (
        <ol className="relative border-l border-[#262626] ml-2 space-y-4">
          {sorted.map((t, i) => {
            const key = t.kind || t.status;
            const def = STYLE[key] || { icon: ClipboardCheck, color: 'text-[#A3A3A3]', label: key };
            const Icon = def.icon;
            return (
              <li key={i} data-testid={`timeline-event-${i}`} className="ml-4 pb-1">
                <span className={`absolute -left-[9px] mt-0.5 w-4 h-4 rounded-full bg-[#0A0A0A] border-2 border-[#262626] flex items-center justify-center ${def.color}`}>
                  <Icon className="w-2.5 h-2.5" />
                </span>
                <p className={`text-sm font-medium ${def.color}`}>{def.label}</p>
                {t.note && <p className="text-[#A3A3A3] text-xs mt-0.5">{t.note}</p>}
                <p className="text-[#525252] text-[10px] uppercase tracking-wider mt-1">
                  {fmt(t.at || t.created_at)} {t.by_name && <span className="ml-1">· by {t.by_name}{t.by_role ? ` (${t.by_role.replace(/_/g, ' ')})` : ''}</span>}
                </p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default JobTimeline;
